import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { THEME_COLOR_NAMES } from '@examdojo/styles';

import {
  DEFAULT_PASCALS_TRIANGLE_PARAMS,
  PascalsTriangleHighlightFn,
  PascalsTriangleParams,
  PascalsTriangleRowHighlight,
  PascalsTriangleSelection,
} from './pascals-triangle.model';

const THEME_COLOR_SET = new Set<string>(THEME_COLOR_NAMES);

function buildTriangle(rows: number): readonly number[][] {
  const tri: number[][] = [];
  for (let n = 0; n < rows; n++) {
    const row: number[] = [1];
    for (let r = 1; r < n; r++) {
      row.push(tri[n - 1][r - 1] + tri[n - 1][r]);
    }
    if (n > 0) {
      row.push(1);
    }
    tri.push(row);
  }
  return tri;
}

@Component({
  selector: 'dojo-pascals-triangle',
  standalone: true,
  templateUrl: './pascals-triangle.component.html',
  styleUrl: './pascals-triangle.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block' },
})
export class PascalsTriangleComponent {
  readonly params = input<PascalsTriangleParams>(DEFAULT_PASCALS_TRIANGLE_PARAMS);
  readonly selection = model<PascalsTriangleSelection | null>(null);
  readonly height = input<number | null>(null);
  readonly selectable = input(true);
  readonly highlightFn = input<PascalsTriangleHighlightFn | null>(null);
  readonly highlightedRows = input<PascalsTriangleRowHighlight[]>([]);
  readonly activeCell = input<{ n: number; r: number } | null>(null);
  readonly hoveredCell = input<{ n: number; r: number } | null>(null);
  readonly activeRIndex = input<number | null>(null);
  readonly showParents = input(false);
  readonly cellHover = output<{ n: number; r: number } | null>();
  readonly cellClick = output<{ n: number; r: number }>();

  private readonly hoveredForParents = signal<{ n: number; r: number } | null>(null);

  readonly parentCells = computed(() => {
    const cell = this.hoveredForParents();
    if (!cell || cell.n < 2 || cell.r === 0 || cell.r === cell.n) {
      return null;
    }
    return [
      { n: cell.n - 1, r: cell.r - 1 },
      { n: cell.n - 1, r: cell.r },
    ] as const;
  });

  readonly plusSignRow = computed(() => {
    const parents = this.parentCells();
    return parents ? parents[0].n : null;
  });

  readonly plusSignCol = computed(() => {
    const parents = this.parentCells();
    return parents ? parents[1].r : null;
  });

  readonly triangle = computed(() => buildTriangle(this.params().rows));
  readonly maxRow = computed(() => this.params().rows - 1);
  readonly rLabels = computed(() => Array.from({ length: this.params().rows }, (_, i) => i));

  private readonly highlightedRowMap = computed(() => {
    const map = new Map<number, PascalsTriangleRowHighlight>();
    for (const h of this.highlightedRows()) {
      map.set(h.row, h);
    }
    return map;
  });

  onCellClick(n: number, r: number): void {
    this.cellClick.emit({ n, r });
    if (!this.selectable()) {
      return;
    }
    const current = this.selection();
    if (current && current.n === n && current.r === r) {
      this.selection.set(null);
    } else {
      this.selection.set({ n, r });
    }
  }

  highlightGroup(n: number, r: number): string | null {
    const fn = this.highlightFn();
    return fn ? fn(n, r) : null;
  }

  rowHighlightColor(n: number): string | null {
    const h = this.highlightedRowMap().get(n);
    return h ? `var(--${h.color}-500)` : null;
  }

  isRowHighlighted(n: number): boolean {
    return this.highlightedRowMap().has(n);
  }

  isThemeColor(value: string | null): boolean {
    return value !== null && THEME_COLOR_SET.has(value);
  }

  cellColorVar(n: number, r: number): string | null {
    const group = this.highlightGroup(n, r);
    if (this.isThemeColor(group)) {
      return `var(--${group}-500)`;
    }
    return this.rowHighlightColor(n);
  }

  isSelected(n: number, r: number): boolean {
    const sel = this.selection();
    return sel !== null && sel.n === n && sel.r === r;
  }

  /**
   * On the highlight path but not the selected cell.
   * Path = left edge (r=0, n=0..sel.n) + selected row (r=0..sel.r).
   */
  isOnPath(n: number, r: number): boolean {
    const sel = this.selection();
    if (!sel) {
      return false;
    }
    if (n === sel.n && r === sel.r) {
      return false;
    }
    if (r === 0 && n <= sel.n) {
      return true;
    }
    if (n === sel.n && r <= sel.r) {
      return true;
    }
    return false;
  }

  /** n-label is active if this row is part of the path (n <= sel.n). */
  isNActive(n: number): boolean {
    const sel = this.selection();
    return sel !== null && n <= sel.n;
  }

  /** r-label is active if it's part of the path (r <= sel.r). */
  isRActive(r: number): boolean {
    const sel = this.selection();
    return sel !== null && r <= sel.r;
  }

  isRLabelHighlighted(r: number): boolean {
    return this.activeRIndex() === r;
  }

  isNHighlighted(n: number): boolean {
    const cell = this.activeCell();
    return cell !== null && cell.n === n;
  }

  isCellActive(n: number, r: number): boolean {
    const cell = this.activeCell();
    if (cell !== null && cell.n === n && cell.r === r) {
      return true;
    }
    const hovered = this.hoveredCell();
    return hovered !== null && hovered.n === n && hovered.r === r;
  }

  isCellDimmed(n: number, _r: number): boolean {
    const cell = this.activeCell();
    if (!cell) {
      return false;
    }
    return n !== cell.n;
  }

  isNDimmed(n: number): boolean {
    const cell = this.activeCell();
    return cell !== null && n !== cell.n;
  }

  isRDimmed(r: number): boolean {
    const cell = this.activeCell();
    return cell !== null && r !== cell.r;
  }

  isParentOfHovered(n: number, r: number): boolean {
    const parents = this.parentCells();
    if (!parents) {
      return false;
    }
    return (parents[0].n === n && parents[0].r === r) || (parents[1].n === n && parents[1].r === r);
  }

  showParentsCellBg(n: number, r: number): string | null {
    if (!this.showParents() || !this.parentCells()) {
      return null;
    }

    const hovered = this.hoveredForParents();
    if (!hovered) {
      return null;
    }

    const isHoveredCell = hovered.n === n && hovered.r === r;
    const isParent = this.isParentOfHovered(n, r);

    if (!isHoveredCell && !isParent) {
      return null;
    }

    const rowHighlight = this.highlightedRowMap().get(n);
    const color = rowHighlight ? rowHighlight.color : 'neutral';

    return `var(--${color}-100)`;
  }

  onCellHover(n: number, r: number): void {
    this.cellHover.emit({ n, r });
    if (this.showParents()) {
      this.hoveredForParents.set({ n, r });
    }
  }

  onCellLeave(): void {
    this.cellHover.emit(null);
    if (this.showParents()) {
      this.hoveredForParents.set(null);
    }
  }
}
